import { useMemo, useRef } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import type Highcharts from "highcharts";
import ReactDOMServer from "react-dom/server";
import { v4 as uuidv4 } from "uuid";

import { combineConfigs } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";

import Chart from "pro-features/components/Chart/src/Chart";
import { CustomLabel } from "pro-features/components/Chart/src/components/column/CustomLabel";
import type { EChartDisplayType } from "pro-features/components/Chart/src/configs/column-chart/columnChartConfig";
import type { ColumnChartDataItems } from "pro-features/components/Chart/src/types";
import { resolveMaxYaxisValue } from "pro-features/components/Chart/src/utils";
import DurationService from "app/services/DurationService";
import { chartTypes } from "app/UtilitiesAndConstants/Constants/ChartTypes";

import { MarketSharesPeriodOverPeriodCategoryValueChange } from "./MarketSharesPeriodOverPeriodCategoryValueChange";
import { MarketSharesPeriodOverPeriodTooltipFormatter } from "./MarketSharesPeriodOverPeriodTooltipFormatter";
import { getDomainSubtitle, getPeriodOverPeriodValueChange } from "./utils/utils";

const defaultConfig: Highcharts.Options = {
    legend: {
        enabled: false,
    },
    xAxis: {
        crosshair: false,
        lineColor: colorsPalettes.carbon[200],
        gridLineWidth: 0,
        tickLength: 5,
        categories: [],
    },
    yAxis: {
        labels: {
            style: {
                fontSize: "11px",
                fontFamily: "Roboto",
                color: colorsPalettes.carbon[400],
            },
        },
        gridLineWidth: 0.5,
        showFirstLabel: true,
        showLastLabel: true,
        gridZIndex: 2,
        tickPixelInterval: 65,
    },
    chart: {
        type: chartTypes.COLUMN,
        animation: false,
        borderColor: colorsPalettes.carbon[0],
        height: 350,
    },
    plotOptions: {
        series: {
            states: {
                hover: {
                    enabled: false,
                },
            },
        },
        column: {
            grouping: true,
            maxPointWidth: 48,
            minPointLength: 3,
            pointPadding: 0.1,
            borderRadius: 4,
        },
    },
};

type PointWithMetadata = {
    regionalDomainsCount: number;
    displayText: string;
    favicon: string;
} & Highcharts.Point;

interface Props {
    chartData: ColumnChartDataItems;
    compareChartData: ColumnChartDataItems;
    chartDisplayType: EChartDisplayType;
    labelFormatter: (value: number) => string;
    config?: Highcharts.Options;
}

export const MarketSharesPeriodOverPeriod = ({
    chartData,
    compareChartData,
    labelFormatter,
    chartDisplayType,
    config = {},
}: Props) => {
    const { params } = useCurrentStateAndParams();
    const { duration, comparedDuration } = params;
    const filters = useMemo(
        () => DurationService.getDurationData(duration, comparedDuration).forAPI,
        [duration, comparedDuration],
    );

    const categories = useMemo(() => chartData.map((point) => point.displayText), [chartData]);
    const key = useMemo(() => uuidv4(), [chartData, compareChartData]);
    const maxYaxisValue = useMemo(
        () => resolveMaxYaxisValue([...chartData, ...compareChartData]),
        [chartData, compareChartData],
    );

    const chartRef = useRef<Highcharts.Chart | null>(null);

    const finalConfig = combineConfigs({}, [
        defaultConfig,
        config,
        {
            chart: {
                events: {
                    load: function () {
                        // eslint-disable-next-line @typescript-eslint/no-this-alias
                        const chart = this;
                        const xAxis = chart.xAxis[0];

                        xAxis.update({
                            labels: {
                                useHTML: true,
                                formatter: function () {
                                    const categoryIndex = this.pos;
                                    const chartDataItem = chartData[categoryIndex];
                                    return ReactDOMServer.renderToString(
                                        <CustomLabel
                                            displayText={chartDataItem.displayText}
                                            subDisplayText={chartDataItem.subDisplayText}
                                            value={labelFormatter(chartDataItem.y)}
                                        />,
                                    );
                                },
                            },
                        });
                    },
                    render: function () {
                        // eslint-disable-next-line @typescript-eslint/no-this-alias
                        const chart = this;
                        const mainSeries = chart.series[1];
                        const comparedSeries = chart.series[0];

                        mainSeries.points.forEach((point, pointIndex) => {
                            const currentPoint = point as PointWithMetadata;
                            const associatedPoint = comparedSeries.points[pointIndex] as PointWithMetadata;
                            const distanceBetweenPoints =
                                associatedPoint.plotX -
                                currentPoint.plotX -
                                currentPoint.shapeArgs.width -
                                chart.options.plotOptions.column.pointPadding * 2;
                            const midX = currentPoint.plotX + currentPoint.shapeArgs.width + distanceBetweenPoints / 2;
                            const tallerColumn =
                                currentPoint.shapeArgs.height > associatedPoint.shapeArgs.height
                                    ? currentPoint
                                    : associatedPoint;
                            const y = tallerColumn.plotY - (currentPoint.favicon ? 60 : 20);
                            const valueChange = getPeriodOverPeriodValueChange(
                                currentPoint.y,
                                associatedPoint.y,
                                chartDisplayType,
                            );
                            const iconFormat = chartData[currentPoint.index].isSegment
                                ? "segment"
                                : currentPoint.regionalDomainsCount > 0
                                  ? "regional"
                                  : "none";
                            const displayValue = ReactDOMServer.renderToString(
                                <MarketSharesPeriodOverPeriodCategoryValueChange
                                    valueChange={valueChange}
                                    iconSrc={currentPoint.favicon}
                                    iconFormat={iconFormat}
                                    isPreviousValueEmpty={associatedPoint.y === 0}
                                />,
                            );
                            chart.renderer.label(displayValue, midX, y, null, null, null, true).add();
                        });
                    },
                },
            },
            xAxis: { categories },
            yAxis: {
                max: maxYaxisValue,
                labels: {
                    formatter: function () {
                        return labelFormatter(this.value as number);
                    },
                },
            },
            tooltip: {
                enabled: true,
                useHTML: true,
                shared: true,
                outside: true,
                formatter: function () {
                    const currentPoint = this as PointWithMetadata;
                    const chart = currentPoint.series.chart;
                    const associatedSeries = chart.series.find((series) => series.name !== currentPoint.series.name);
                    const associatedPoint = associatedSeries.points[currentPoint.index];

                    const currentPointValue = chartData[currentPoint.index].y;
                    const associatedPointValue = compareChartData[associatedPoint.index].y;
                    const regionalDomainsCount =
                        chartData[currentPoint.index].regionalDomainsCount ??
                        compareChartData[currentPoint.index].regionalDomainsCount ??
                        currentPoint.regionalDomainsCount;
                    const subDisplayText = getDomainSubtitle(undefined, regionalDomainsCount);
                    const valueChange = getPeriodOverPeriodValueChange(
                        currentPointValue,
                        associatedPointValue,
                        chartDisplayType,
                    );

                    return ReactDOMServer.renderToString(
                        <MarketSharesPeriodOverPeriodTooltipFormatter
                            websiteTitle={currentPoint.displayText}
                            websiteIcon={currentPoint.favicon}
                            subDisplayText={subDisplayText}
                            currentValue={currentPointValue}
                            comparedValue={associatedPointValue}
                            labelFormatter={labelFormatter}
                            valueChange={valueChange}
                            filters={filters}
                            color={chartData[currentPoint.index].color}
                        />,
                    );
                },
            },
        },
    ]);

    return (
        <Chart
            type={chartTypes.COLUMN}
            data={[{ data: compareChartData }, { data: chartData }]}
            config={finalConfig}
            key={key}
            afterRender={(chart) => (chartRef.current = chart)}
        />
    );
};
